import { Button, Space } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import BackButtonIcon from "../../../../assets/icons/BackButtonIcon";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import LogIcon from "../../../../assets/icons/LogIcon";
import UserIcon from "../../../../assets/icons/UserIcon";
import AdjustmentLog from "../../../../components/Settings/Leave/AdjustmentLog";
import UpdateLeaveTypeModal from "../../../../components/Settings/Leave/UpdateLeaveTypeModal";
import EmployeeInfoItem from "../../../../components/common/EmployeeInfoItem";
import InfoItem from "../../../../components/common/InfoItem";
import InfoTitle from "../../../../components/common/InfoTitle";
import LogList from "../../../../components/common/LogList";
import TopCard from "../../../../components/common/TopCard";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import Loader from "../../../../components/ui/Loader";
import { dateFormat } from "../../../../constants/constants";
import {
  useDeleteLeaveProrateMutation,
  useGetLeaveProrateDetailsQuery,
} from "../../../../features/leaveProrate/leaveProrateApi";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { formatDate } from "../../../../lib/time";
import { toast } from "../../../../lib/toast";
import { GetLeaveProrateDetailsType } from "../../../../types";
import { backLink } from "../../../../utils/utils";
import NotFound from "../../../NotFound";
import { leaveSettingsTabList } from "./LeaveType";

export default function LeaveProrateDetails() {
  const { detailsId, viewId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { open, handleOpen, handleClose } = useModal<
    GetLeaveProrateDetailsType["data"]["business_member_leave_type"],
    "CONFIRM"
  >();
  const { isLoading, isError, error, data } = useGetLeaveProrateDetailsQuery(
    Number(detailsId),
    {
      skip: !detailsId,
    }
  );
  const prorateEmployee = data?.data?.business_member_leave_type;

  const [
    deleteLeaveProrate,
    {
      isError: isDeleteError,
      error: deleteError,
      isLoading: isDeleteLoading,
      isSuccess: isDeleteSuccess,
    },
  ] = useDeleteLeaveProrateMutation();
  useError(isError, error, "Get leave details data fail");
  useError(isDeleteError, deleteError, "Delete leave prorate fail");

  const handleDelete = () => {
    if (detailsId)
      deleteLeaveProrate({
        business_member_leave_type_ids: [detailsId],
      });
  };

  // success toast message
  useEffect(() => {
    if (isDeleteSuccess) {
      toast("success", "Leave prorate deleted successfully");
      handleClose();
      navigate(`${backLink(location.pathname, 2)}/view/${viewId}`);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeleteSuccess, location.pathname, navigate]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Leave Settings" tabList={leaveSettingsTabList}>
      {open?.type === "CONFIRM" ? (
        <ConfirmModal
          open={open?.open}
          handleClose={handleClose}
          handleOk={handleDelete}
          questionText="Are sure to delete this?"
          okText="Confirm"
          okLoading={isDeleteLoading}
        />
      ) : null}

      <TopCard
        leftChildren={
          <Link to={`${backLink(location.pathname, 2)}/view/${viewId}`}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        <Button
          type="primary"
          onClick={() => {
            handleOpen({
              type: "UPDATE",
              data: prorateEmployee,
            });
          }}
        >
          Update
        </Button>
        <Button
          className="delete-button"
          icon={<DeleteIcon />}
          onClick={() =>
            detailsId &&
            handleOpen({
              type: "CONFIRM",
            })
          }
        >
          Delete
        </Button>
      </TopCard>

      {isLoading ? (
        <Loader />
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="p-6 bg-white rounded-md border border-extra-light">
            <Space
              className="w-full mb-6 justify-between"
              align="center"
              size={8}
            >
              <InfoTitle
                buttonClassName="disabled:!text-neutral"
                icon={<UserIcon />}
              >
                Employee Info
              </InfoTitle>
            </Space>

            <EmployeeInfoItem
              img={prorateEmployee?.employee_info?.pro_pic}
              name={prorateEmployee?.employee_info?.name}
              id={prorateEmployee?.employee_info?.employee_id}
              designation={prorateEmployee?.employee_info?.designation}
              department={prorateEmployee?.employee_info?.department}
              email={prorateEmployee?.employee_info?.email}
            />

            <div className="mt-4">
              <InfoItem title="Joined">
                {formatDate(prorateEmployee?.created_at)}
              </InfoItem>
              <InfoItem title="Prorate Type">
                {prorateEmployee?.is_auto_prorated
                  ? "Auto Prorate"
                  : "Manual Prorate"}
              </InfoItem>
              <InfoItem title="Type">
                {prorateEmployee?.leave_type.title}
              </InfoItem>
              <InfoItem title="Updated Balance">
                {prorateEmployee?.remaining_days || 0} Days
              </InfoItem>

              <InfoItem title="Updated by" rootClassName="border-b-0 !pb-0">
                {prorateEmployee?.updated_by
                  ? prorateEmployee?.updated_by_name
                  : "N/A"}
              </InfoItem>
            </div>
          </div>

          <div>
            <LogList
              list={data?.data?.prorate_logs?.map((item) => ({
                id: item?.id,
                date: dayjs(item?.created_at, "DD/MM/YY")?.format(dateFormat),
                log: item?.log,
              }))}
            />
            {/* <div className="p-6 bg-white rounded-md border border-extra-light">
              <InfoTitle
                className="mb-6"
                buttonClassName="disabled:!text-neutral"
                icon={<LogIcon />}
              >
                Log
              </InfoTitle>

              <div className="max-h-[400px] overflow-y-auto">
                {data?.data?.prorate_logs?.map((item) => (
                  <div
                    className="flex items-center py-4 first:pt-0 last:pb-0 border-b border-extra-light last:border-b-0"
                    key={item?.id}
                  >
                    <div className="w-9">
                      <span className="w-2.5 h-2.5 bg-complete rounded-full inline-block" />
                    </div>
                    <div className="flex-1">
                      <h6 className="text-tint-neutral text-sm font-semibold mb-[3px]">
                        {dayjs(item?.created_at, "DD/MM/YY")?.format(
                          dateFormat
                        )}
                      </h6>

                      <p className="text-display-body leading-7">{item?.log}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div> */}

            <div className="p-6 bg-white rounded-md border border-extra-light mt-6">
              <InfoTitle
                className="mb-6"
                buttonClassName="disabled:!text-neutral"
                icon={<LogIcon />}
              >
                Adjustment Log
              </InfoTitle>

              <AdjustmentLog
                businessMemberId={Number(viewId)}
                leaveTypeId={Number(prorateEmployee?.leave_type?.id)}
              />
            </div>
          </div>
        </div>
      )}

      {open.type === "UPDATE" && (
        <UpdateLeaveTypeModal
          open={open.open}
          type={open.type}
          data={open?.data}
          handleClose={handleClose}
        />
      )}
    </SidebarLayout>
  );
}

// const log = [
//   {
//     time: "06:01 PM - 06 Nov, 2024",
//     details: "Baker Hossain canceled this leave",
//   },
//   {
//     time: "06:01 PM - 06 Nov, 2024",
//     details: "Super Admin changed this leave status from Pending to Approved",
//   },
//   {
//     time: "06:01 PM - 06 Nov, 2024",
//     details: "Super Admin changed this leave status from Pending to Approved",
//   },
// ];
